import React, {Fragment, useEffect} from "react";
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {Modal} from "../common/Modal";
import {Constants} from "../common/Constants";
import {UpdateNotTrackedInvoice} from "./UpdateNotTrackedInvoice";
import {UpdateTrackedInvoice} from "./UpdateTrackedInvoice";
import {SelectUtility} from "../common/SelectUtility";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {SubscriptionDataUtility} from "../common/SubscriptionDataUtility";
import {StringUtility} from "../common/StringUtility";
import {DateUtility} from "../common/DateUtility";
import {
    ExternalQboInvoice,
    ExternalStripeInvoice,
    FpInvoice,
    FpInvoiceEstimatePhase,
    FpInvoiceItems,
    Invoice, InvoiceSyncStatusData, InvoiceUnsyncedData
} from "./InvoiceTypes";
import {PillButton} from "../common/PillButton";

interface SingleInvoiceProps {
    cancel: Function;
    invoiceId?: number;
    fpInvoiceId?: number;
    afterSuccessfulSave: Function;
    afterSuccessfulCancel: Function;
    onClickMakeSubscription?: Function;
    goToSubscription?: Function;
}

interface SingleInvoiceData {
    loading: boolean;
    invoice?: Invoice;
    fpInvoice?: FpInvoice;
    fpInvoiceItems?: FpInvoiceItems;
    fpInvoiceDataAnchor?: string;
    stripeInvoice?: ExternalStripeInvoice;
    qboInvoice?: ExternalQboInvoice;
    unsyncedData?: InvoiceUnsyncedData;
    syncStatusData?: InvoiceSyncStatusData;
}

export let SingleInvoice = function (props: SingleInvoiceProps) {
    const initialData: SingleInvoiceData = {
        loading: true,
        invoice: undefined
    }
    const renderableData = useRenderableData(initialData);

    // on mount, fetch model from API and put it in state
    useEffect(function () {
        loadInvoice();
    }, []);

    function loadInvoice() {
        renderableData.updateAndRender({loading: true})
        Promise.resolve({})
            .then(function () {
                if (props.invoiceId) {
                    return ApiUtility.getInvoice(props.invoiceId)
                } else {
                    return ApiUtility.getInvoiceByFpId(props.fpInvoiceId)
                }
            })
            .then(function (invoiceData) {
                let fpInvoice = invoiceData.fp_invoice;
                let fpInvoiceItems: FpInvoiceItems[] = [];

                (fpInvoice?.invoiceFinancialDetails?.estimatePhases || []).forEach(
                    (estimatePhase: FpInvoiceEstimatePhase, phaseIndex: number) => {
                        if (estimatePhase.IS) {
                            estimatePhase.IS.forEach((subLine) => {
                                fpInvoiceItems.push({
                                    ...subLine,
                                    phaseDescription: estimatePhase.description,
                                    phaseId: estimatePhase.id,
                                    phaseName: estimatePhase.name,
                                    phaseTotal: estimatePhase.total,
                                    fpServiceType: Constants.fpServiceTypeNames.internalService,
                                })
                            })
                        }
                        if (estimatePhase.IE) {
                            estimatePhase.IE.forEach((subLine) => {
                                fpInvoiceItems.push({
                                    ...subLine,
                                    phaseDescription: estimatePhase.description,
                                    phaseId: estimatePhase.id,
                                    phaseName: estimatePhase.name,
                                    phaseTotal: estimatePhase.total,
                                    fpServiceType: Constants.fpServiceTypeNames.internalExpense,
                                })
                            })
                        }
                        if (estimatePhase.EE) {
                            estimatePhase.EE.forEach((subLine) => {
                                fpInvoiceItems.push({
                                    ...subLine,
                                    phaseDescription: estimatePhase.description,
                                    phaseId: estimatePhase.id,
                                    phaseName: estimatePhase.name,
                                    phaseTotal: estimatePhase.total,
                                    fpServiceType: Constants.fpServiceTypeNames.externalExpense,
                                })
                            })
                        }
                    }
                )

                renderableData.updateAndRender({
                    fpInvoiceDataAnchor: `invoice-fp-data-${fpInvoice?.invoicenumber}`,
                    fpInvoice: fpInvoice,
                    invoice: invoiceData.invoice,
                    stripeInvoice: invoiceData.stripeInvoice,
                    qboInvoice: invoiceData.qboInvoice,
                    fpInvoiceItems: fpInvoiceItems,
                    unsyncedData: invoiceData.unsyncedData,
                    syncStatusData: invoiceData.syncStatusData,
                    loading: false,
                });
            })
            .catch(function (error) {
                renderableData.updateAndRender({loading: false});
                console.log(`Error fetching invoice: `, error);
                MessageDisplayerUtility.error(`Error fetching invoice.`);
            });
    }

    return (
        <Fragment>
            <ConditionalRenderer if={renderableData.loading} renderer={() => (
                <OverlayLoadingIndicator overlayClass="single-invoice-loading-indicator-overlay"/>
            )}/>
            <ConditionalRenderer if={renderableData.invoice} renderer={() => {
                let invoice = renderableData.invoice as Invoice;
                let modalBoxClass = "single-invoice-modal-box ";
                let invoiceStatusName = invoice.invoice_status.name;
                let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status);

                let invoiceHeaderMain = null;
                let headerClasses = "single-invoice-header-info "
                if (invoice.subscription) {
                    headerClasses += 'multi-level-display'
                    let subscription = invoice.subscription
                    let totalDisplay = SubscriptionDataUtility.getSubscriptionTotalDisplay(subscription)
                    let invoiceTitle = SubscriptionDataUtility.getSubscriptionInvoiceTitle(subscription, invoice);

                    invoiceHeaderMain = (
                        <div className="invoice-header-main">
                            <div className="invoice-header-main-content">
                                <div className="invoice-header-text">
                                    {invoiceTitle}
                                </div>
                                <div
                                    className="invoice-basics"
                                    onClick={() => props.goToSubscription ? props.goToSubscription(subscription.id) : null}
                                >
                                    <span className="invoice-basics-link-content">
                                        <span className="invoice-basics-link-main">
                                            {`(${subscription.ext_stripe_customer.name} - ${totalDisplay})`}
                                        </span>
                                        <img
                                            src="/static/images/chevron-right-textcolor.svg"
                                            className="invoice-basics-icon"
                                        />
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                } else if (renderableData.fpInvoice) {
                    let invoiceNumberToShow =
                        invoice.custom_invoice_number ||
                        renderableData.fpInvoice.invoicenumber;

                    let invoiceNumberDisplay = `Invoice ${invoiceNumberToShow}`
                    if (invoice?.invoice_status?.name === Constants.invoiceStatusNames.notTracked) {
                        invoiceNumberDisplay = 'FP ' + invoiceNumberDisplay
                    }
                    let basics = renderableData.fpInvoice?.invoicecompany?.companyname
                    if (
                        basics &&
                        (
                            invoice?.billed_total ||
                            renderableData.fpInvoice?.total
                        )
                    ) {
                        basics += ' - ' + StringUtility.stringNumberToDisplayCurrency(
                            invoice?.billed_total ||
                            renderableData.fpInvoice?.total
                        )
                    }
                    if (
                        invoice.custom_invoice_number &&
                        invoice.custom_invoice_number !== renderableData.fpInvoice.invoicenumber
                    ) {
                        headerClasses += 'multi-level-display'
                        invoiceHeaderMain = (
                            <div className="invoice-header-main">
                                <div className="invoice-header-main-content">
                                    <div className="invoice-header-text">
                                        {invoiceNumberDisplay}
                                    </div>
                                    <div className="invoice-basics">
                                        <ConditionalRenderer if={basics} renderer={() => (
                                            <span className="invoice-basics-main">{basics}</span>
                                        )}/>
                                        <a
                                            className="invoice-basics-link"
                                            href={`#${renderableData.fpInvoiceDataAnchor}`}
                                        >
                                            <span className="invoice-basics-link-main">
                                                {`(From FP invoice ${renderableData.fpInvoice.invoicenumber})`}
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )
                    } else {
                        if (invoice.invoice_status.additional_info) {
                            headerClasses += 'multi-level-display'
                        }
                        invoiceHeaderMain = (
                            <div className="invoice-header-main">
                                <div className="invoice-header-main-content">
                                    <div className="invoice-header-text">
                                        {invoiceNumberDisplay}
                                    </div>
                                    <div className="invoice-basics">
                                        <span className="invoice-basics-main">{basics}</span>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                }

                let modalProps = {
                    headerTextContentRenderer: () => {
                        return (
                            <div className={headerClasses}>
                                {invoiceHeaderMain}
                                <div className="single-invoice-header-details">
                                    <div className="single-invoice-header-details-content">
                                        <div className="single-invoice-header-status">
                                            <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                                                {invoiceStatusName}
                                            </div>
                                        </div>
                                        <div className="single-invoice-header-additional-info">
                                            {invoice.invoice_status.additional_info}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    },
                    onClickCloseOption: props.cancel,
                    boxClass: modalBoxClass
                }

                // show different content for different invoice states
                let modalContent = null;
                if (invoiceStatusName === Constants.invoiceStatusNames.notTracked && renderableData.fpInvoice) {
                    modalProps.boxClass += "not-tracked"
                    return (
                        <UpdateNotTrackedInvoice
                            invoice={invoice}
                            fpInvoice={renderableData.fpInvoice}
                            fpInvoiceItems={renderableData.fpInvoiceItems}
                            afterSuccessfulSave={props.afterSuccessfulSave}
                            onClickMakeSubscription={props.onClickMakeSubscription}
                            modalProps={modalProps}
                        />
                    )
                } else if (
                    invoiceStatusName === Constants.invoiceStatusNames.billed ||
                    invoiceStatusName === Constants.invoiceStatusNames.paid
                ) {
                    return (
                        <UpdateTrackedInvoice
                            invoice={invoice}
                            fpInvoice={renderableData.fpInvoice}
                            fpInvoiceItems={renderableData.fpInvoiceItems}
                            fpInvoiceDataAnchor={renderableData.fpInvoiceDataAnchor}
                            stripeInvoice={renderableData.stripeInvoice}
                            qboInvoice={renderableData.qboInvoice}
                            afterSuccessfulSave={props.afterSuccessfulSave}
                            afterSuccessfulCancel={props.afterSuccessfulCancel}
                            unsyncedData={renderableData.unsyncedData}
                            syncStatusData={renderableData.syncStatusData}
                            modalProps={modalProps}
                        />
                    )
                } else {
                    return (
                        <Modal {...modalProps}>
                            <div className="single-invoice">
                                <div className="single-invoice-content">
                                    <div className="invoice-unsupported-status-message">
                                        Unsupported invoice status.
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    )
                }
            }}/>
        </Fragment>
    );
};


